<template>
  <div class="google-rating-section">
    <div class="rating-header">
      <v-icon color="#4285f4" class="mr-2">mdi-google</v-icon>
      <h3 class="rating-title">Google Rating</h3>
    </div>

    <div class="rating-content">
      <!-- Current Rating Display -->
      <div v-if="currentRating" class="current-rating">
        <div class="rating-stars-display">
          <v-icon 
            v-for="star in 5" 
            :key="'display-' + star"
            :color="star <= currentRating ? '#fbbc04' : '#e0e0e0'"
            size="32"
          >
            {{ getStarIcon(star, currentRating) }}
          </v-icon>
        </div>
        <div class="rating-value">{{ currentRating }} / 5</div>
        <div class="rating-date">{{ ratingDate }}</div>
      </div>

      <!-- No Rating Yet -->
      <div v-else class="no-rating">
        <v-icon color="#9e9e9e" size="48">mdi-star-outline</v-icon>
        <p class="no-rating-text">No rating yet</p>
      </div>

      <!-- Interactive Rating Selector -->
      <div class="rating-selector">
        <div class="selector-label">Set customer rating:</div>
        <div class="rating-stars-interactive">
          <button
            v-for="star in 5"
            :key="'star-' + star"
            class="star-btn"
            :class="{ 'star-hover': hoveredStar >= star, 'star-selected': selectedRating >= star }"
            @click="selectRating(star)"
            @mouseenter="hoveredStar = star"
            @mouseleave="hoveredStar = 0"
          >
            <v-icon 
              :color="getStarColor(star)"
              size="40"
            >
              {{ getInteractiveStarIcon(star) }}
            </v-icon>
          </button>
        </div>
        
        <div v-if="selectedRating > 0" class="rating-labels">
          <span class="rating-label">{{ ratingLabels[selectedRating - 1] }}</span>
        </div>

        <!-- Action Buttons -->
        <div class="rating-actions">
          <v-btn
            v-if="selectedRating > 0"
            color="primary"
            @click="saveRating"
            :loading="saving"
          >
            <v-icon left>mdi-check</v-icon>
            Save Rating
          </v-btn>
          <v-btn
            v-if="selectedRating > 0"
            text
            @click="clearRating"
          >
            <v-icon left>mdi-close</v-icon>
            Clear
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GoogleRatingSelector',
  
  props: {
    value: {
      type: Number,
      default: null
    },
    /*ratedAt: {
      type: String,
      default: null
    }*/
  },

  data() {
    return {
      selectedRating: 0,
      hoveredStar: 0,
      saving: false,
      ratingLabels: [
        'Very Poor',
        'Poor',
        'Average',
        'Good',
        'Excellent'
      ]
    };
  },

  computed: {
    currentRating() {
      return this.value;
    },
    
    /*ratingDate() {
      if (!this.ratedAt) return '';
      return new Date(this.ratedAt).toLocaleDateString('hu-HU');
    }*/
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.selectedRating = val || 0;
      }
    }
  },

  methods: {
    selectRating(rating) {
      this.selectedRating = rating;
    },

    getStarIcon(star, rating) {
      return star <= rating ? 'mdi-star' : 'mdi-star-outline';
    },

    getInteractiveStarIcon(star) {
      if (this.selectedRating >= star || this.hoveredStar >= star) {
        return 'mdi-star';
      }
      return 'mdi-star-outline';
    },

    getStarColor(star) {
      if (this.selectedRating >= star || this.hoveredStar >= star) {
        return '#fbbc04'; // Google yellow
      }
      return '#e0e0e0';
    },

    async saveRating() {
      this.saving = true;
      
      try {
        this.$emit('save', this.selectedRating);
        this.$emit('input', this.selectedRating);
        
        // Success feedback
        setTimeout(() => {
          this.saving = false;
        }, 500);
      } catch (error) {
        console.error('Error saving rating:', error);
        this.saving = false;
      }
    },

    clearRating() {
      //this.selectedRating = 0;
      this.$emit('clear');
      //this.$emit('input', null);
    }
  }
};
</script>

<style scoped>
.google-rating-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 16px;
}

.rating-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.rating-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.rating-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Current Rating Display */
.current-rating {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.rating-stars-display {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}

.rating-value {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}

.rating-date {
  font-size: 13px;
  color: #999;
}

/* No Rating */
.no-rating {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  background: #fafafa;
  border-radius: 8px;
  border: 2px dashed #e0e0e0;
}

.no-rating-text {
  margin: 12px 0 0 0;
  color: #999;
  font-size: 14px;
}

/* Interactive Rating Selector */
.rating-selector {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.selector-label {
  font-size: 14px;
  font-weight: 500;
  color: #666;
}

.rating-stars-interactive {
  display: flex;
  gap: 8px;
}

.star-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  transition: transform 0.2s ease;
  border-radius: 50%;
}

.star-btn:hover {
  transform: scale(1.1);
}

.star-btn:active {
  transform: scale(0.95);
}

.rating-labels {
  min-height: 24px;
  display: flex;
  align-items: center;
}

.rating-label {
  font-size: 16px;
  font-weight: 600;
  color: #fbbc04;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Action Buttons */
.rating-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

/* Responsive */
@media (max-width: 600px) {
  .google-rating-section {
    padding: 16px;
  }
  
  .rating-stars-interactive .star-btn v-icon {
    font-size: 32px !important;
  }
}
</style>